/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import type { DateFieldState, DateSegmentState } from './useDateField';
import { BCP47Locale } from '#UNSAFE_IntlDateTime';
import { CalendarDate, EditableDateSegment, IsoMonth } from './types';
import { getMonthName, getTodayAsCalendarDate } from '#utils/PRIVATE_dayUtils';
import { getDaysInMonth, getMonthsInYear, MAX_ISO_YEAR } from '#utils/UNSAFE_calendarDateUtils';

type DateFieldActionData = Partial<DateSegmentState> & {
  direction?: 'increase' | 'decrease';
  hasLeadingZero?: boolean;
  locale?: BCP47Locale;
};

type DateFieldAction = {
  actionType:
    | 'clearDate'
    | 'clearSegment'
    | 'commit'
    | 'goToEnd'
    | 'goToHome'
    | 'page'
    | 'selectDateOrSegment'
    | 'selectFirst'
    | 'selectNext'
    | 'selectPrevious'
    | 'selectSegment'
    | 'step'
    | 'updateSegment';
  data?: DateFieldActionData;
};

type ResetAction = {
  actionType: 'reset';
  data: DateFieldState;
};

type DateReducerAction = DateFieldAction | ResetAction;

/**
 * Reducer function for updating DateFieldState per the specified DateFieldAction.
 * @param state
 * @param action
 */
const dateReducer = (state: DateFieldState, action: DateReducerAction) => {
  // On mount, the reducer is initialized with initial state derived from the DateField's value.
  // If that value is changed programmatically afterwards, the only way to push that update
  // into the reducer is to define a "reset" action.
  if (action.actionType === 'reset') {
    return action.data;
  }

  const segmentType = action.data?.type!;
  const segmentState = findSegment(state.segments, segmentType);

  switch (action.actionType) {
    case 'clearDate': {
      const updatedSegments = selectFirst(clearDate(state.segments), state.orderedSegments);
      return createDateFieldStateWithSelectedDate(state, updatedSegments, false);
    }
    case 'clearSegment': {
      const updatedSegment = clearSegment(segmentState);
      return createDateFieldState(state, segmentType, updatedSegment);
    }
    case 'commit': {
      const updatedSegments = clearSelectionAndComplete(state.segments);
      return createDateFieldStateWithSelectedDate(state, updatedSegments, false);
    }
    case 'goToEnd': {
      const updatedSegment = goToEnd(
        segmentType,
        segmentState,
        state.segments,
        action.data?.locale!
      );
      return createDateFieldState(state, segmentType, updatedSegment, false);
    }
    case 'goToHome': {
      const updatedSegment = goToHome(
        segmentType,
        segmentState,
        state.segments,
        action.data?.locale!
      );
      return createDateFieldState(state, segmentType, updatedSegment, false);
    }
    case 'page': {
      const direction = action.data?.direction;
      const locale = action.data?.locale!;
      const updatedSegment = doStepOrPage(
        segmentType,
        segmentState,
        state.segments,
        'page',
        direction,
        locale
      );
      return createDateFieldState(state, segmentType, updatedSegment, false);
    }
    case 'selectDateOrSegment': {
      return selectDateOrSegment(state);
    }
    case 'selectFirst': {
      const updatedSegments = selectFirst(state.segments, state.orderedSegments);
      return createDateFieldStateWithSelectedDate(state, updatedSegments, false);
    }
    case 'selectNext': {
      const updatedSegments = selectNext(state.segments, state.orderedSegments);
      return createDateFieldStateWithSelectedDate(state, updatedSegments, false);
    }
    case 'selectPrevious': {
      const updatedSegments = selectPrevious(state.segments, state.orderedSegments);
      return createDateFieldStateWithSelectedDate(state, updatedSegments, false);
    }
    case 'selectSegment': {
      const updatedSegments = selectSegment(state.segments, segmentType);
      return createDateFieldStateWithSelectedDate(state, updatedSegments, false);
    }
    case 'step': {
      const direction = action.data?.direction;
      const locale = action.data?.locale!;
      const updatedSegment = doStepOrPage(
        segmentType,
        segmentState,
        state.segments,
        'step',
        direction,
        locale
      );
      return createDateFieldState(state, segmentType, updatedSegment, false);
    }
    case 'updateSegment': {
      const updatedSegment = updateSegment(segmentType, segmentState, action, state.segments);
      return createDateFieldState(state, segmentType, updatedSegment);
    }
    default: {
      throw new Error('Unknown action type');
    }
  }
};

const stringToNumber = (s: string) => +s;
const numberToString = (n: number) => n.toString();
const withinRange = (n: number, min: number, max: number) => n >= min && n <= max;

const findSegment = (segments: DateSegmentState[], type: EditableDateSegment): DateSegmentState => {
  return segments.find((segment) => segment.type === type)!;
};

// A segment is considered complete if appending a zero to the current value causes it
// to exceed the max. For instance, if you type 2-9 into month or 4-9 into day, the
// segment is complete because you can't type any more digits and have it be a valid value.
const isSegmentComplete = (val: number, max: number) => {
  const num = stringToNumber(numberToString(val) + '0');
  return num > max;
};

// A date is considered complete if all of its segments have values.
const isDateComplete = (allSegments: DateSegmentState[]) => {
  return allSegments.every((seg) => seg.text && seg.text.length > 0);
};

// A date is considered a partial date if at least one segment has a value.
const isPartialDate = (allSegments: DateSegmentState[]) => {
  return allSegments.some((seg) => seg['aria-valuenow'] !== undefined);
};

// Construct a calendar date that reflects the values of each segment.
// If none of the segments have a value, return undefined.
const createCalendarDateFromSegments = (segments: DateSegmentState[]) => {
  const hasAnySegmentWithAValue = isPartialDate(segments);
  if (!hasAnySegmentWithAValue) {
    return undefined;
  }

  // This returns something that looks like [ {month: 12}, {day: 25}, {year: 2020} ].
  // Note: we use the aria-valuenow to get the current value as a number (not string).
  const segmentValues = segments.map((seg) => {
    return { [seg.type]: seg['aria-valuenow'] };
  });

  // Iterate over segmentValues and spread each value to get {month: 12, day:25, year: 2020}.
  const iterator = segmentValues.values();
  let cd: CalendarDate = {};
  for (const segValue of iterator) {
    cd = { ...cd, ...segValue };
  }
  return cd;
};

// Find the current value for year.
const getYearValue = (segments: DateSegmentState[]) => {
  const yearState = findSegment(segments, 'year');
  return yearState['aria-valuenow'];
};

// Find the current value for month.
const getMonthValue = (segments: DateSegmentState[]) => {
  const monthState = findSegment(segments, 'month');
  return monthState['aria-valuenow'] as IsoMonth;
};

// Determine the aria-valuetext for the given month, which includes the month name.
const getMonthAria = (locale: BCP47Locale, month: IsoMonth, year: number | undefined) => {
  const monthName = getMonthName(locale, month, year ?? '*', 'long');
  return `${month} - ${monthName}`;
};

// Return a copy of the ordered segments array.
const getOrderedSegments = (currentState: DateFieldState) => {
  return [...currentState.orderedSegments];
};

// Replace the updated segment and return a new DateSegmentState[].
const replaceDateSegment = (
  existingSegments: DateSegmentState[],
  updatedSegmentType: EditableDateSegment,
  updatedSegment: DateSegmentState
) => {
  return existingSegments.map((seg) => (seg.type === updatedSegmentType ? updatedSegment : seg));
};

// Return a new DateSegmentState[] that reflects changes across all segments.
// When you change the month this can affect the day, for instance.
const createDateSegmentStates = (
  existingSegments: DateSegmentState[],
  orderedSegments: EditableDateSegment[],
  updatedSegmentType: EditableDateSegment,
  updatedSegment: DateSegmentState,
  autoAdvance: boolean
) => {
  const modifiedSegments = replaceDateSegment(existingSegments, updatedSegmentType, updatedSegment);
  let updatedSegments = modifiedSegments;

  if (updatedSegmentType === 'month' || updatedSegmentType === 'year') {
    // If month was changed, update the day as needed; this includes adjusting the
    // day value if it now exceeds the max number of days in the month.
    // If year was changed, that can also affect the max number of days (if month is February).
    const daySeg = findSegment(modifiedSegments, 'day');
    if (daySeg) {
      const monthValue =
        updatedSegmentType == 'month'
          ? (updatedSegment['aria-valuenow'] as IsoMonth)
          : getMonthValue(modifiedSegments);
      const yearValue =
        updatedSegmentType === 'year'
          ? updatedSegment['aria-valuenow']
          : getYearValue(modifiedSegments);
      const dayMax = getDaysInMonth(monthValue ?? '*', yearValue ?? '*');
      const currentDay = daySeg['aria-valuenow'];
      const dayValue = !!currentDay && currentDay > dayMax ? dayMax : currentDay;
      const dayValueStr = !!dayValue ? numberToString(dayValue) : undefined;
      const updatedDaySeg = {
        ...daySeg,
        'aria-valuemax': dayMax,
        'aria-valuenow': dayValue,
        'aria-valuetext': dayValueStr,
        text: dayValueStr
      };
      updatedSegments = replaceDateSegment(modifiedSegments, 'day', updatedDaySeg);
    }
  }

  // If the updatedSegment is complete, automatically select the next segment.
  updatedSegments =
    updatedSegment.isComplete && autoAdvance
      ? selectNext(updatedSegments, orderedSegments)
      : updatedSegments;

  return updatedSegments;
};

// Construct a new DateFieldState from a set of updated segments.
const createDateFieldStateFromSegments = (
  currentState: DateFieldState,
  updatedSegments: DateSegmentState[]
) => {
  return {
    currentDate: createCalendarDateFromSegments(updatedSegments),
    isCompleteDate: isDateComplete(updatedSegments),
    isDateSelected: currentState.isDateSelected,
    isPartialDate: isPartialDate(updatedSegments),
    orderedSegments: getOrderedSegments(currentState),
    segments: updatedSegments
  };
};

// Construct a new DateFieldState given an updated segment, which may affect other segments.
const createDateFieldState = (
  currentState: DateFieldState,
  updatedSegmentType: EditableDateSegment,
  updatedSegment: DateSegmentState,
  autoAdvance = true
): DateFieldState => {
  const updatedSegments = createDateSegmentStates(
    currentState.segments,
    currentState.orderedSegments,
    updatedSegmentType,
    updatedSegment,
    autoAdvance
  );

  return createDateFieldStateFromSegments(currentState, updatedSegments);
};

// Return a new copy of the segment with the value cleared.
const clearSegment = (segmentState: DateSegmentState) => {
  return {
    ...segmentState,
    'aria-valuetext': undefined,
    'aria-valuenow': undefined,
    text: undefined,
    isComplete: false
  };
};

// Return a new copy of segments with all values cleared and aria-valuemax adjusted.
const clearDate = (segments: DateSegmentState[]) => {
  return segments.map((segmentState) => {
    const cleared = clearSegment(segmentState);
    const valueMax =
      cleared.type === 'year'
        ? MAX_ISO_YEAR
        : cleared.type === 'month'
        ? getMonthsInYear('*')
        : getDaysInMonth('*', '*');
    return { ...cleared, 'aria-valuemax': valueMax };
  });
};

// Return a new copy of the segment with updated values.
const updateSegment = (
  segmentType: EditableDateSegment,
  segmentState: DateSegmentState,
  action: DateFieldAction,
  segments: DateSegmentState[]
) => {
  const actionText = action.data?.text!;
  const hasLeadingZero = action.data?.hasLeadingZero;
  const locale = action.data?.locale!;
  const minValue = segmentState['aria-valuemin']!;
  const maxValue = segmentState['aria-valuemax']!;
  const wasComplete = segmentState.isComplete;

  // If the segment was already complete, replace any existing text with actionText.
  // Otherwise append actionText onto existing text to get the requested string.
  // For instance if the existing text is '1' and actionText (i.e. that the user typed)
  // is '2' then requestedValStr is '12'.
  const requestedValStr = wasComplete ? actionText : segmentState.text + actionText;

  // If the requested value is out of range, discard the existing text/value and only
  // keep the new one. Check if the segment is complete.
  const requestedVal = stringToNumber(requestedValStr);
  const inRange = withinRange(requestedVal, minValue, maxValue);
  const newValStr = inRange ? requestedValStr : actionText;
  const newVal = inRange ? requestedVal : stringToNumber(actionText);
  const isComplete =
    isSegmentComplete(newVal, maxValue) || (!!hasLeadingZero && segmentType !== 'year');

  // For month changes, aria-valuetext includes the month name.
  const ariaValueText =
    segmentType === 'month'
      ? getMonthAria(locale, newVal as IsoMonth, getYearValue(segments))
      : newValStr;

  // Create a new segment state that reflects the updated value.
  return {
    ...segmentState,
    'aria-valuetext': ariaValueText,
    'aria-valuenow': newVal,
    text: newValStr,
    isComplete
  };
};

// Return a new DateSegmentState[] where the specified segment is selected, all others cleared.
const selectSegment = (segments: DateSegmentState[], segmentType: EditableDateSegment) => {
  return segments.map((seg) => {
    return { ...seg, isSelected: seg.type === segmentType ? true : false };
  });
};

// Return a new DateSegmentState[] where the first segment is selected.
const selectFirst = (segments: DateSegmentState[], orderedSegments: EditableDateSegment[]) => {
  return selectSegment(segments, orderedSegments[0]);
};

// Find the index of the selected segment in the ordered list; return -1 if there is no selection.
const getSelectedSegmentIndex = (
  segments: DateSegmentState[],
  orderedSegments: EditableDateSegment[]
) => {
  const sel = segments.find((seg) => seg.isSelected === true);
  return sel === undefined ? -1 : orderedSegments.findIndex((type) => type === sel.type);
};

// Convenience function to mark the selected segment complete.
const markSelectedSegmentComplete = (segments: DateSegmentState[]) => {
  return segments.map((seg) => (seg.isSelected ? { ...seg, isComplete: true } : seg));
};

// Select the segment following the one that is currently selected.
const selectNext = (segments: DateSegmentState[], orderedSegments: EditableDateSegment[]) => {
  const selectedIndex = getSelectedSegmentIndex(segments, orderedSegments);
  const max = orderedSegments.length - 1;

  // If we're already at the last position, do nothing. Note if nothing was currently selected
  // then selectedIndex is -1. The first segment will be selected.
  return selectedIndex === max
    ? segments
    : selectSegment(markSelectedSegmentComplete(segments), orderedSegments[selectedIndex + 1]);
};

// Select the segment prior to the one that is currently selected.
const selectPrevious = (segments: DateSegmentState[], orderedSegments: EditableDateSegment[]) => {
  const selectedIndex = getSelectedSegmentIndex(segments, orderedSegments);
  return selectedIndex === 0
    ? segments
    : selectedIndex === -1
    ? selectFirst(segments, orderedSegments)
    : selectSegment(markSelectedSegmentComplete(segments), orderedSegments[selectedIndex - 1]);
};

// Return a new DateSegmentState[] where all segments are unselected and any values are
// marked complete. We want to do this when the user commits the DateField value (on blur).
const clearSelectionAndComplete = (segments: DateSegmentState[]) => {
  return segments.map((seg) => {
    return { ...seg, isSelected: false, isComplete: !!seg.text };
  });
};

// Return a new DateSegmentState[] where all segments are unselected.
const clearSelection = (segments: DateSegmentState[]) => {
  return segments.map((seg) => {
    return { ...seg, isSelected: false };
  });
};

// Return a new DateFieldState where the entire date is selected if the date is complete,
// or the first segment is selected.
const selectDateOrSegment = (currentState: DateFieldState) => {
  if (currentState.isCompleteDate) {
    return createDateFieldStateWithSelectedDate(
      currentState,
      clearSelection(currentState.segments),
      true
    );
  } else {
    const updatedSegments = selectFirst(currentState.segments, currentState.orderedSegments);
    return createDateFieldStateFromSegments(currentState, updatedSegments);
  }
};

// Create a new DateFieldState from the updatedSegments and isDateSelected params.
const createDateFieldStateWithSelectedDate = (
  currentState: DateFieldState,
  updatedSegments: DateSegmentState[],
  isDateSelected: boolean
) => {
  return {
    currentDate: createCalendarDateFromSegments(updatedSegments),
    isCompleteDate: isDateComplete(updatedSegments),
    isDateSelected,
    isPartialDate: isPartialDate(updatedSegments),
    orderedSegments: getOrderedSegments(currentState),
    segments: updatedSegments
  };
};

const YEAR_HOME_VALUE = 1900;
const YEAR_END_VALUE = 2100;

// If a segment is empty, use today's date as a starting point for any step or page action.
const getDefaultSegmentValue = (segmentType: EditableDateSegment) => {
  const { year: yearDefault, month: monthDefault, day: dayDefault } = getTodayAsCalendarDate();
  const defaults = { year: yearDefault, month: monthDefault, day: dayDefault };
  return defaults[segmentType];
};

// Get the minimum value for the segment.
const getHomeSegmentValue = (segmentType: EditableDateSegment, segmentState: DateSegmentState) => {
  return segmentType === 'year' ? YEAR_HOME_VALUE : segmentState['aria-valuemin']!;
};

// Get the maximum value for the segment (for year, this is YEAR_END_VALUE).
const getEndSegmentValue = (segmentType: EditableDateSegment, segmentState: DateSegmentState) => {
  return segmentType === 'year' ? YEAR_END_VALUE : segmentState['aria-valuemax']!;
};

// Return the result of stepping currentValue in the requested direction, taking into account min and max.
const getSteppedSegmentValue = (
  currentValue: number,
  direction: DateFieldActionData['direction'],
  min: number,
  max: number
) => {
  const newValue =
    direction === 'increase'
      ? currentValue + 1 > max
        ? min
        : currentValue + 1
      : currentValue - 1 < min
      ? max
      : currentValue - 1;
  return newValue;
};

// Return the result of paging the value up or down, taking into account min and max.
const getPagedSegmentValue = (
  currentValue: number,
  direction: DateFieldActionData['direction'],
  min: number,
  max: number,
  segmentType: EditableDateSegment
) => {
  const step = segmentType === 'month' ? 2 : segmentType === 'year' ? 5 : 7;
  const newValue =
    direction === 'increase'
      ? currentValue + step > max
        ? (currentValue + step) % max
        : currentValue + step
      : currentValue - step < min
      ? currentValue - step + max
      : currentValue - step;
  return newValue;
};

// Create a new segment with an updated value from a keyboard action such as step, page, Home, or End.
const createSegmentFromKeyboardAction = (
  newVal: number,
  locale: BCP47Locale,
  segmentType: EditableDateSegment,
  segmentState: DateSegmentState,
  segments: DateSegmentState[]
) => {
  const newValStr = numberToString(newVal);
  // For month changes, aria-valuetext includes the month name.
  const ariaValueText =
    segmentType === 'month'
      ? getMonthAria(locale, newVal as IsoMonth, getYearValue(segments))
      : newValStr;

  // Create a new segment state that reflects the updated value.
  // After any keyboard action, the value is complete so that if the user types anything,
  // it will replace the stepped value.
  return {
    ...segmentState,
    'aria-valuetext': ariaValueText,
    'aria-valuenow': newVal,
    text: newValStr,
    isComplete: true
  };
};

// Set the segment to its minimum value.
const goToHome = (
  segmentType: EditableDateSegment,
  segmentState: DateSegmentState,
  segments: DateSegmentState[],
  locale: BCP47Locale
) => {
  const newValue = getHomeSegmentValue(segmentType, segmentState);
  return createSegmentFromKeyboardAction(newValue, locale, segmentType, segmentState, segments);
};

// Set the segment to its maximum value.
const goToEnd = (
  segmentType: EditableDateSegment,
  segmentState: DateSegmentState,
  segments: DateSegmentState[],
  locale: BCP47Locale
) => {
  const newValue = getEndSegmentValue(segmentType, segmentState);
  return createSegmentFromKeyboardAction(newValue, locale, segmentType, segmentState, segments);
};

// Step or page a segment's value.
const doStepOrPage = (
  segmentType: EditableDateSegment,
  segmentState: DateSegmentState,
  segments: DateSegmentState[],
  type: 'step' | 'page',
  direction: DateFieldActionData['direction'],
  locale: BCP47Locale
) => {
  const valueNow = segmentState['aria-valuenow'];
  const min = segmentState['aria-valuemin']!;
  const max = segmentState['aria-valuemax']!;
  const newValue =
    valueNow === undefined
      ? getDefaultSegmentValue(segmentType)
      : type === 'page'
      ? getPagedSegmentValue(valueNow, direction, min, max, segmentType)
      : getSteppedSegmentValue(valueNow, direction, min, max);

  return createSegmentFromKeyboardAction(newValue, locale, segmentType, segmentState, segments);
};

export { dateReducer, getMonthAria, numberToString, stringToNumber };
export type { DateFieldAction, DateFieldActionData };
